import { normalizeResponse } from 'utils';
import { installmentLabel } from 'utils/billing';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

// Relatorios do menu Cobranca. Mesma ideia de utils/billing.js: logica pura
// (query, linhas, cards, colunas de exportacao), e a chamada fica no hook.

// Le todas as paginas de um endpoint paginado. Os relatorios usam com
// `GET /titulos`, `/log-cobrancas` e `/tentativa-cobrancas`. O backend devolve no maximo 1000 por pagina, com
// `pagination.pageCount`; um mes de baixas raramente passa disso, mas um
// periodo longo passa. `isError` decide se a resposta e erro (e se avisa).
export const readAllPages = async (fetchPage, isError) => {
  const items = [];
  let page = 1;
  let pageCount = 1;
  do {
    const result = await fetchPage(page);
    if (isError(result)) {
      return null;
    }
    items.push(...normalizeResponse(result));
    pageCount =
      (result?.pagination ?? result?.meta?.pagination)?.pageCount || 1;
    page += 1;
  } while (page <= pageCount);
  return items;
};

// --- Relatorio de baixas ------------------------------------------------

// O relatorio de baixas le `GET /titulos`, e nao os logs: o titulo guarda o
// estado final da baixa (ver services/titulo.js). O tipo vem do `tipo_baixa`
// e e filtrado na query.
export const SETTLEMENT_TYPES = {
  payment: 'normal',
  renegotiated: 'renegociado',
  cancelled: 'cancelado',
  all: 'todos',
};

export const SETTLEMENT_TYPE_OPTIONS = [
  { value: SETTLEMENT_TYPES.payment, label: 'Pagamentos' },
  { value: SETTLEMENT_TYPES.renegotiated, label: 'Renegociadas' },
  { value: SETTLEMENT_TYPES.cancelled, label: 'Canceladas' },
  { value: SETTLEMENT_TYPES.all, label: 'Todas' },
];

const TYPE_LABELS = {
  normal: 'Pagamento',
  renegociado: 'Renegociação',
  cancelado: 'Cancelamento',
};

// Origem gravada no log da baixa. Valor desconhecido aparece como veio.
const ORIGIN_LABELS = {
  tela: 'Tela',
  planilha: 'Planilha',
  arquivo_omni: 'Arquivo Omni',
  cron: 'Automático',
  api: 'API',
};

const pad = (value) => `${value}`.padStart(2, '0');

const toIsoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Periodo padrao: do dia 1 do mes corrente ate hoje.
export const currentMonthRange = () => {
  const today = new Date();
  return {
    from: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: toIsoDate(today),
  };
};

// `AAAA-MM-DD` com ano de 4 digitos. O campo de data dispara mudanca a cada
// digito do ano (0002, 0020...), e sem essa trava cada um viraria uma consulta.
const isIsoDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value || '') && Number(value.slice(0, 4)) >= 2000;

export const isValidRange = ({ from, to }) =>
  isIsoDate(from) && isIsoDate(to) && from <= to;

export const buildSettlementQuery = ({ from, to, type, page = 1 }) =>
  [
    'filters[status][$eq]=baixado',
    type && type !== SETTLEMENT_TYPES.all
      ? `filters[tipo_baixa][$eq]=${type}`
      : null,
    `filters[data_baixa][$gte]=${from}`,
    `filters[data_baixa][$lte]=${to}`,
    'sort[0]=data_baixa:desc',
    'sort[1]=id:desc',
    `pagination[page]=${page}`,
    'pagination[pageSize]=1000',
  ]
    .filter(Boolean)
    .reduce((query, part, index) => `${query}${index ? '&' : '?'}${part}`, '');

const toNumber = (value) => {
  const parsed =
    typeof value === 'string' ? Number(value.replace(',', '.')) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

const round2 = (value) => Math.round(value * 100) / 100;

// Dias corridos entre duas datas `AAAA-MM-DD`, sem fuso.
const daysBetween = (from, to) => {
  if (!isIsoDate(from) || !isIsoDate(to)) {
    return null;
  }
  const utc = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((utc(to) - utc(from)) / 86400000);
};

export const formatIsoDate = (iso) =>
  isIsoDate(`${iso}`.slice(0, 10))
    ? `${iso}`.slice(0, 10).split('-').reverse().join('/')
    : '';

export const money = (value) =>
  Number.isFinite(value) ? parseCurrency(value) : '';

const relationId = (value) =>
  value && typeof value === 'object' ? value.id : value;

// Indice titulo -> log da baixa mais recente. Os logs chegam em `data:desc`,
// entao o primeiro de cada titulo e o da baixa vigente (houve estorno e
// rebaixa, vale a ultima). O log por contrato da baixa em lote nao tem titulo
// e fica de fora.
export const buildSettlementLogIndex = (logs) =>
  (logs || []).reduce((index, log) => {
    const tituloId = relationId(log?.titulo);
    if (tituloId && !index[tituloId]) {
      index[tituloId] = log;
    }
    return index;
  }, {});

// Uma linha por titulo baixado. Os valores ficam numericos na linha (a
// ordenacao compara o valor cru) e o texto formatado vai ao lado, em
// `*_fmt`, para a busca global achar "1.234,56".
export const buildSettlementRows = (titulos, logIndex = {}) =>
  (titulos || []).map((titulo) => {
    const cobranca = titulo?.cobranca || {};
    const cliente = cobranca?.cliente || {};
    const log = logIndex[titulo?.id];

    const valor = toNumber(titulo?.valor);
    const recebido = toNumber(titulo?.valor_recebido);
    const juros = toNumber(titulo?.valor_juros_mora) || 0;
    const multa = toNumber(titulo?.valor_multa) || 0;
    const iof = toNumber(titulo?.valor_iof_juros) || 0;
    const encargos = round2(juros + multa + iof);

    // Desconto: o gravado na baixa, quando o backend devolve. Sem ele, o que
    // faltou para a parcela (valor - recebido), so em pagamento: na
    // renegociacao e no cancelamento o recebido e zero e nao ha desconto.
    const storedDiscount = toNumber(titulo?.valor_desconto);
    const derivedDiscount =
      titulo?.tipo_baixa === 'normal' && valor !== null && recebido !== null
        ? Math.max(0, round2(valor - recebido))
        : null;
    const desconto = storedDiscount ?? derivedDiscount;
    const percDesconto =
      toNumber(titulo?.perc_desconto) ??
      (desconto && valor ? round2((desconto / valor) * 100) : null);

    const dataBaixa = titulo?.data_baixa
      ? `${titulo.data_baixa}`.slice(0, 10)
      : '';
    const dataVencimento = titulo?.data_vencimento
      ? `${titulo.data_vencimento}`.slice(0, 10)
      : '';

    return {
      id: titulo?.id,
      cobrancaId: cobranca?.id ?? null,
      contrato: cobranca?.contrato || '',
      numero_titulo: titulo?.numero_titulo || '',
      customer: normalizeName(cliente?.nome_completo) || '',
      document: cliente?.cpf || cliente?.cnpj || '',
      distributor: normalizeName(cobranca?.distribuidor?.trade_name) || '',
      banco: normalizeName(cobranca?.banco?.nome_fantasia) || '',
      parcela: installmentLabel(titulo) ?? '',
      entrada: titulo?.entrada === true,
      data_vencimento: dataVencimento,
      data_baixa: dataBaixa,
      // Positivo: pago depois do vencimento; negativo: antecipado.
      dias_pagamento: daysBetween(dataVencimento, dataBaixa),
      valor,
      valor_fmt: money(valor),
      juros,
      multa,
      iof,
      encargos,
      encargos_fmt: money(encargos),
      desconto,
      desconto_fmt: money(desconto),
      perc_desconto: percDesconto,
      valor_recebido: recebido,
      valor_recebido_fmt: money(recebido),
      tipo_baixa: titulo?.tipo_baixa || '',
      tipo_baixa_label:
        TYPE_LABELS[titulo?.tipo_baixa] || titulo?.tipo_baixa || '',
      mora_dispensada: titulo?.mora_dispensada === true,
      operador:
        log?.usuario_email ||
        log?.usuario?.email ||
        titulo?.importacao_usuario ||
        '',
      origem: log?.origem ? ORIGIN_LABELS[log.origem] || log.origem : '',
      registrada_em: log?.data || '',
    };
  });

const sum = (rows, field) =>
  round2(
    (rows || []).reduce(
      (total, row) => total + (Number.isFinite(row?.[field]) ? row[field] : 0),
      0
    )
  );

// Cards do relatorio, sobre as linhas visiveis (depois da busca global).
export const buildSettlementCards = (rows) => {
  const list = rows || [];
  const contracts = new Set(list.map((row) => row.cobrancaId)).size;
  const valor = sum(list, 'valor');
  const desconto = sum(list, 'desconto');
  const late = list.filter((row) => row.dias_pagamento > 0).length;
  const withDiscount = list.filter((row) => row.desconto > 0).length;
  return [
    {
      label: 'Valor recebido',
      valor: parseCurrency(sum(list, 'valor_recebido')),
      rodape: `Parcelas: ${parseCurrency(valor)}`,
      cor: '#00D32A',
    },
    {
      label: 'Títulos baixados',
      valor: list.length,
      rodape: `${contracts} contrato${contracts === 1 ? '' : 's'}`,
      cor: '#2670D1',
    },
    {
      label: 'Encargos',
      valor: parseCurrency(sum(list, 'encargos')),
      rodape: 'Juros, multa e IOF de atraso',
      cor: '#FF9500',
    },
    {
      label: 'Descontos',
      valor: parseCurrency(desconto),
      rodape: `${withDiscount} título${withDiscount === 1 ? '' : 's'}${
        valor ? ` · ${round2((desconto / valor) * 100)}% das parcelas` : ''
      }`,
      cor: '#dd4952',
    },
    {
      label: 'Pagos em atraso',
      valor: late,
      rodape: list.length
        ? `${round2((late / list.length) * 100)}% dos títulos`
        : '-',
      cor: '#7B44F2',
    },
  ];
};

const decimal = (value) =>
  Number.isFinite(value) ? `${value}`.replace('.', ',') : '';

// Colunas da exportacao: tudo o que a linha tem, inclusive o que nao cabe na
// tela (numero do titulo, juros/multa/IOF separados, ids). Os valores vao como
// numero com virgula, para o Excel somar.
export const SETTLEMENT_EXPORT_COLUMNS = [
  { title: 'Data da baixa', value: (row) => formatIsoDate(row.data_baixa) },
  {
    title: 'Registrada em',
    value: (row) => formatadores.TIMESTAMP_TO_BR_FULL(row.registrada_em),
  },
  { title: 'Contrato', value: (row) => row.contrato },
  { title: 'Número do título', value: (row) => row.numero_titulo },
  { title: 'Parcela', value: (row) => row.parcela },
  { title: 'Cliente', value: (row) => row.customer },
  { title: 'CPF/CNPJ', value: (row) => row.document },
  { title: 'Distribuidora', value: (row) => row.distributor },
  { title: 'Banco', value: (row) => row.banco },
  { title: 'Vencimento', value: (row) => formatIsoDate(row.data_vencimento) },
  { title: 'Dias após o vencimento', value: (row) => row.dias_pagamento ?? '' },
  { title: 'Valor da parcela', value: (row) => decimal(row.valor) },
  { title: 'Juros de mora', value: (row) => decimal(row.juros) },
  { title: 'Multa', value: (row) => decimal(row.multa) },
  { title: 'IOF de atraso', value: (row) => decimal(row.iof) },
  { title: 'Desconto', value: (row) => decimal(row.desconto) },
  { title: '% Desconto', value: (row) => decimal(row.perc_desconto) },
  { title: 'Valor recebido', value: (row) => decimal(row.valor_recebido) },
  { title: 'Tipo de baixa', value: (row) => row.tipo_baixa_label },
  {
    title: 'Mora dispensada',
    value: (row) => (row.mora_dispensada ? 'Sim' : ''),
  },
  { title: 'Operador', value: (row) => row.operador },
  { title: 'Origem', value: (row) => row.origem },
  { title: 'Id do título', value: (row) => row.id },
  { title: 'Id da cobrança', value: (row) => row.cobrancaId },
];

// --- Desempenho por operador --------------------------------------------

// Quem ve o painel de todos os operadores: so a flag, comparada com `true`
// estrito, como as demais flags do Billing. Sem ela o operador ve so os
// proprios numeros. ATENCAO: o recorte e so de tela; as rotas de tentativas e
// logs devolvem os registros de toda a distribuidora.
export const canSeeAllOperators = (user) => user?.desempenho_cobranca === true;

// Dias de folga depois da `data_promessa` em que o pagamento ainda conta como
// promessa cumprida (o boleto compensa depois do pagamento).
export const PROMISE_TOLERANCE_DAYS = 3;

// Resultados em que houve conversa com o cliente.
const EFFECTIVE_RESULTS = [
  'contato_efetivo',
  'promessa_pagamento',
  'acordo_fechado',
  'recusa_pagamento',
];

export const ATTEMPT_RESULTS = {
  contato_efetivo: { label: 'Contato efetivo', color: '#2670D1' },
  promessa_pagamento: { label: 'Promessa de pagamento', color: '#7B44F2' },
  acordo_fechado: { label: 'Acordo fechado', color: '#00D32A' },
  recusa_pagamento: { label: 'Recusa de pagamento', color: '#dd4952' },
  nao_atendeu: { label: 'Não atendeu', color: '#a4a4a4' },
  caixa_postal: { label: 'Caixa postal', color: '#cbd5e0' },
  telefone_invalido: { label: 'Telefone inválido', color: '#FF9500' },
  cliente_nao_localizado: { label: 'Cliente não localizado', color: '#f6ad55' },
  outro: { label: 'Outro', color: '#718096' },
};

// Logs que entram no painel. `cancelamento_renegociacao` e lido para saber
// quais renegociacoes do operador foram desfeitas depois, mesmo fora do
// periodo, por isso os logs sao buscados do inicio do periodo ate hoje.
export const PERFORMANCE_LOG_ACTIONS = [
  'baixa',
  'renegociacao',
  'cancelamento_renegociacao',
  'alteracao_status',
];

// Limites do dia local em ISO: a `data` de tentativa e de log e datetime em
// UTC, e comparar com `AAAA-MM-DD` cortaria 3 horas do dia no Brasil.
export const dayStartIso = (iso) => new Date(`${iso}T00:00:00`).toISOString();
export const dayEndIso = (iso) => new Date(`${iso}T23:59:59.999`).toISOString();

// Dia local `AAAA-MM-DD` de um datetime.
const localDay = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : toIsoDate(date);
};

const addDays = (iso, days) => {
  const [year, month, day] = iso.split('-').map(Number);
  return toIsoDate(new Date(year, month - 1, day + days));
};

const userEmail = (item) =>
  `${item?.usuario_email || item?.usuario?.email || ''}`.trim().toLowerCase();

const userId = (item) => relationId(item?.usuario) ?? null;

// Chave do operador. O `usuario_email` e uma copia gravada no momento da acao
// e pode nao bater com o e-mail do usuario em outro registro (o mesmo
// operador aparecia duas vezes no seletor). Por isso a chave e o id do
// usuario, e o e-mail so entra quando o registro nao traz o id e nenhum outro
// registro liga aquele e-mail a um id. `user` (o logado) tambem entra no
// indice, para o recorte "so os meus numeros" achar os registros dele.
export const createOperatorResolver = ({ attempts, logs, user }) => {
  const idByEmail = {};
  const link = (email, id) => {
    if (email && id !== null && id !== undefined && !idByEmail[email]) {
      idByEmail[email] = id;
    }
  };
  link(`${user?.email || ''}`.trim().toLowerCase(), user?.id);
  [...(attempts || []), ...(logs || [])].forEach((item) => {
    link(userEmail(item), userId(item));
    // O e-mail da relacao tambem liga, alem da copia gravada.
    link(`${item?.usuario?.email || ''}`.trim().toLowerCase(), userId(item));
  });
  return (item) => {
    const id = userId(item) ?? idByEmail[userEmail(item)];
    return id !== null && id !== undefined ? `u${id}` : userEmail(item);
  };
};

const userName = (item) =>
  item?.usuario?.nome ||
  item?.usuario?.username ||
  item?.usuario_email ||
  item?.usuario?.email ||
  '';

const percent = (part, total) => (total ? round2((part / total) * 100) : null);

// Promessa: cumprida se algum titulo do contrato (ou o proprio titulo, quando
// a tentativa aponta um) foi baixado entre o dia da tentativa e a
// `data_promessa` mais a tolerancia; pendente enquanto o prazo nao venceu.
const promiseStatus = (attempt, paidByContract, today) => {
  const promised = `${attempt?.data_promessa || ''}`.slice(0, 10);
  if (!isIsoDate(promised)) {
    return null;
  }
  const start = localDay(attempt.data);
  const deadline = addDays(promised, PROMISE_TOLERANCE_DAYS);
  const tituloId = relationId(attempt?.titulo);
  const paid = (paidByContract[relationId(attempt?.cobranca)] || []).some(
    (titulo) =>
      (!tituloId || titulo.id === tituloId) &&
      titulo.data_baixa >= start &&
      titulo.data_baixa <= deadline
  );
  if (paid) {
    return 'kept';
  }
  return today <= deadline ? 'pending' : 'broken';
};

// Destinos que contam como encaminhamento (ver Forward, services/cobranca.js).
const FORWARD_STATUSES = ['notificacao', 'execucao'];

// Status novo de um log `alteracao_status`. O `dados` guarda o antes/depois,
// e o formato varia entre as rotas, por isso le os caminhos possiveis. Sem
// nenhum deles, cai na `descricao`, o texto pronto do log.
const forwardDestination = (log) => {
  const dados = log?.dados || {};
  const candidates = [
    dados?.depois?.status,
    dados?.status_novo,
    dados?.novo_status,
    dados?.para,
    dados?.status,
  ].map((value) => `${value || ''}`.toLowerCase());
  const found = candidates.find((value) => FORWARD_STATUSES.includes(value));
  if (found) {
    return found;
  }
  if (candidates.some(Boolean)) {
    return null;
  }
  const descricao = `${log?.descricao || ''}`.toLowerCase();
  if (/execu[cç][aã]o/.test(descricao)) {
    return 'execucao';
  }
  return /notifica[cç][aã]o/.test(descricao) ? 'notificacao' : null;
};

const emptyOperator = (key, name, email) => ({
  id: key,
  operator: name || email || key,
  email,
  attempts: 0,
  effective: 0,
  agreements: 0,
  promises: 0,
  promisesKept: 0,
  promisesBroken: 0,
  promisesPending: 0,
  renegotiations: 0,
  renegotiatedVolume: 0,
  entries: 0,
  waivedLateFees: 0,
  cancelledRenegotiations: 0,
  settlements: 0,
  settledVolume: 0,
  forwards: 0,
});

// Consolida as tres fontes por operador (chave: ver createOperatorResolver).
// Devolve a linha de cada operador, a serie diaria de tentativas e a
// distribuicao dos resultados. `operator` (a chave) recorta tudo num operador.
export const buildOperatorPerformance = ({
  attempts,
  logs,
  titulos,
  period,
  operator = '',
  keyOf,
  today = toIsoDate(new Date()),
}) => {
  const { from, to } = period;
  const inPeriod = (value) => {
    const day = localDay(value);
    return day >= from && day <= to;
  };
  const only = (item) => !operator || keyOf(item) === operator;

  const paidByContract = {};
  const titulosById = {};
  (titulos || []).forEach((titulo) => {
    titulosById[titulo.id] = titulo;
    const cobrancaId = relationId(titulo?.cobranca);
    if (cobrancaId && titulo?.data_baixa) {
      (paidByContract[cobrancaId] = paidByContract[cobrancaId] || []).push({
        id: titulo.id,
        data_baixa: `${titulo.data_baixa}`.slice(0, 10),
      });
    }
  });

  // Cobrancas novas cujas renegociacoes foram canceladas depois.
  const cancelledCharges = new Set(
    (logs || [])
      .filter((log) => log?.acao === 'cancelamento_renegociacao')
      .map((log) => relationId(log?.cobranca))
  );

  const forwarded = new Set();
  const operators = {};
  const ensure = (item) => {
    const key = keyOf(item);
    if (!key) {
      return null;
    }
    if (!operators[key]) {
      operators[key] = emptyOperator(key, userName(item), userEmail(item));
    }
    // O nome vem do `usuario` populado; o primeiro registro pode ter so o
    // e-mail, entao o nome e completado quando aparecer.
    if (item?.usuario?.nome && !operators[key].named) {
      operators[key].operator = item.usuario.nome;
      operators[key].named = true;
    }
    return operators[key];
  };

  const days = {};
  for (let day = from; day <= to; day = addDays(day, 1)) {
    days[day] = {
      day,
      label: formatIsoDate(day).slice(0, 5),
      effective: 0,
      notEffective: 0,
    };
  }
  const results = {};

  (attempts || []).filter(only).forEach((attempt) => {
    const row = ensure(attempt);
    if (!row) {
      return;
    }
    const effective = EFFECTIVE_RESULTS.includes(attempt?.resultado);
    row.attempts += 1;
    row.effective += effective ? 1 : 0;
    row.agreements += attempt?.resultado === 'acordo_fechado' ? 1 : 0;
    if (attempt?.resultado === 'promessa_pagamento') {
      const status = promiseStatus(attempt, paidByContract, today);
      if (status) {
        row.promises += 1;
        row.promisesKept += status === 'kept' ? 1 : 0;
        row.promisesBroken += status === 'broken' ? 1 : 0;
        row.promisesPending += status === 'pending' ? 1 : 0;
      }
    }
    const day = days[localDay(attempt?.data)];
    if (day) {
      day[effective ? 'effective' : 'notEffective'] += 1;
    }
    const result = attempt?.resultado || 'outro';
    results[result] = (results[result] || 0) + 1;
  });

  (logs || [])
    .filter((log) => only(log) && inPeriod(log?.data))
    .forEach((log) => {
      const dados = log?.dados || {};
      if (log?.acao === 'renegociacao') {
        const row = ensure(log);
        if (!row) {
          return;
        }
        // Logs anteriores a 20/10/2026 guardam os parametros em
        // `dados.parametros`, com `entrada` no lugar de `valor_entrada`.
        const params = { ...dados, ...(dados.parametros || {}) };
        const newCharge =
          relationId(log?.cobranca_nova) ?? params.cobranca_nova;
        row.renegotiations += 1;
        row.renegotiatedVolume += toNumber(params.valor_renegociado) || 0;
        row.entries += toNumber(params.valor_entrada ?? params.entrada) || 0;
        row.waivedLateFees += toNumber(params.mora_dispensada_total) || 0;
        row.cancelledRenegotiations += cancelledCharges.has(newCharge) ? 1 : 0;
      } else if (log?.acao === 'baixa') {
        // So a baixa feita na tela, com titulo: o log por contrato da baixa em
        // lote nao tem titulo. O valor vem do titulo, que guarda o estado
        // final (o estornado nao volta como baixado em `GET /titulos`).
        const titulo = titulosById[relationId(log?.titulo)];
        if (log?.origem !== 'tela' || !titulo) {
          return;
        }
        const row = ensure(log);
        if (!row) {
          return;
        }
        row.settlements += 1;
        row.settledVolume += toNumber(titulo?.valor_recebido) || 0;
      } else if (log?.acao === 'alteracao_status') {
        // Encaminhamento e so a mudanca para a esteira juridica. O log
        // `alteracao_status` tambem sai em outras acoes (a renegociacao marca a
        // original como `renegociado`, a baixa que encerra o contrato o
        // finaliza), e um encaminhamento pode gerar mais de um registro: conta
        // uma vez por operador, contrato e destino.
        const destination = forwardDestination(log);
        if (!destination) {
          return;
        }
        const row = ensure(log);
        const forwardKey = `${row?.id}#${relationId(log?.cobranca)}#${destination}`;
        if (row && !forwarded.has(forwardKey)) {
          forwarded.add(forwardKey);
          row.forwards += 1;
        }
      }
    });

  const rows = Object.values(operators)
    .map(({ named: _named, ...row }) => ({
      ...row,
      renegotiatedVolume: round2(row.renegotiatedVolume),
      entries: round2(row.entries),
      waivedLateFees: round2(row.waivedLateFees),
      settledVolume: round2(row.settledVolume),
      effectiveRate: percent(row.effective, row.attempts),
      // A promessa no prazo nao entra na taxa: ainda pode ser cumprida.
      promiseRate: percent(
        row.promisesKept,
        row.promisesKept + row.promisesBroken
      ),
    }))
    .sort((a, b) => b.attempts - a.attempts);

  // Rotulo do eixo dos graficos: primeiro e ultimo nome, que cabem. Se dois
  // operadores derem o mesmo rotulo, entra o e-mail para separar (o eixo de
  // categorias juntaria os dois numa barra so).
  const shortLabel = (row) => {
    const parts = `${row.operator}`.split('@')[0].trim().split(/\s+/);
    return parts.length > 1
      ? `${parts[0]} ${parts[parts.length - 1]}`
      : parts[0];
  };
  const labelCount = rows.reduce((count, row) => {
    const label = shortLabel(row);
    count[label] = (count[label] || 0) + 1;
    return count;
  }, {});
  rows.forEach((row) => {
    const label = shortLabel(row);
    row.axisLabel =
      labelCount[label] > 1 ? `${label} (${row.email || row.id})` : label;
  });

  return {
    rows,
    daily: Object.values(days),
    results: Object.entries(results)
      .map(([id, value]) => ({
        id,
        value,
        label: ATTEMPT_RESULTS[id]?.label || id,
        color: ATTEMPT_RESULTS[id]?.color || '#718096',
      }))
      .sort((a, b) => b.value - a.value),
  };
};

// Operadores para o seletor, a partir das tentativas e dos logs, com a mesma
// chave do painel. Vale o nome do `usuario` populado, quando algum registro
// trouxer; senao, o primeiro rotulo encontrado.
export const listOperators = ({ attempts, logs, keyOf }) => {
  const seen = {};
  [...(attempts || []), ...(logs || [])].forEach((item) => {
    const key = keyOf(item);
    if (key && (!seen[key] || item?.usuario?.nome)) {
      seen[key] = item?.usuario?.nome || seen[key] || userName(item) || key;
    }
  });
  return Object.entries(seen)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildPerformanceCards = (rows) => {
  const total = (field) => sum(rows, field);
  const attempts = total('attempts');
  const effective = total('effective');
  const kept = total('promisesKept');
  const broken = total('promisesBroken');
  const pending = total('promisesPending');
  const renegotiations = total('renegotiations');
  const cancelled = total('cancelledRenegotiations');
  return [
    {
      label: 'Tentativas',
      valor: attempts,
      rodape: `${total('agreements')} acordo(s) fechado(s)`,
      cor: '#2670D1',
    },
    {
      label: 'Contato efetivo',
      valor: attempts ? `${percent(effective, attempts)}%` : '-',
      rodape: `${effective} de ${attempts}`,
      cor: '#7B44F2',
    },
    {
      label: 'Promessas cumpridas',
      valor: kept + broken ? `${percent(kept, kept + broken)}%` : '-',
      rodape: `${kept} de ${kept + broken}${
        pending ? ` · ${pending} no prazo` : ''
      }`,
      cor: '#00D32A',
    },
    {
      label: 'Renegociado',
      valor: parseCurrency(total('renegotiatedVolume')),
      rodape: `${renegotiations} renegociação(ões)${
        cancelled ? ` · ${cancelled} cancelada(s)` : ''
      }`,
      cor: '#FF9500',
    },
    {
      label: 'Baixas manuais',
      valor: parseCurrency(total('settledVolume')),
      rodape: `${total('settlements')} título(s)`,
      cor: '#dd4952',
    },
  ];
};

export const PERFORMANCE_EXPORT_COLUMNS = [
  { title: 'Operador', value: (row) => row.operator },
  { title: 'E-mail', value: (row) => row.email },
  { title: 'Tentativas', value: (row) => row.attempts },
  { title: 'Contatos efetivos', value: (row) => row.effective },
  { title: '% Contato efetivo', value: (row) => decimal(row.effectiveRate) },
  { title: 'Acordos fechados', value: (row) => row.agreements },
  { title: 'Promessas', value: (row) => row.promises },
  { title: 'Promessas cumpridas', value: (row) => row.promisesKept },
  { title: 'Promessas quebradas', value: (row) => row.promisesBroken },
  { title: 'Promessas no prazo', value: (row) => row.promisesPending },
  { title: '% Promessas cumpridas', value: (row) => decimal(row.promiseRate) },
  { title: 'Renegociações', value: (row) => row.renegotiations },
  {
    title: 'Valor renegociado',
    value: (row) => decimal(row.renegotiatedVolume),
  },
  { title: 'Entradas', value: (row) => decimal(row.entries) },
  { title: 'Mora dispensada', value: (row) => decimal(row.waivedLateFees) },
  {
    title: 'Renegociações canceladas',
    value: (row) => row.cancelledRenegotiations,
  },
  { title: 'Baixas manuais', value: (row) => row.settlements },
  { title: 'Valor baixado', value: (row) => decimal(row.settledVolume) },
  { title: 'Encaminhamentos', value: (row) => row.forwards },
];
